import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Footer from '../../components/Footer';
import { downloadReferralPDF } from '../../utils/pdfGenerator';

const SubmissionConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [submissionData, setSubmissionData] = useState(null);
  const [practitionerData, setPractitionerData] = useState(null);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  useEffect(() => {
    console.log('🔍 Submission Confirmation - Receiving state data');
    
    // Get actual form data from location state
    const pdfData = location?.state?.pdfData;
    const practitioner = location?.state?.practitionerData;
    const savedReferralId = location?.state?.referralId;
    const savedReferral = location?.state?.savedReferral;
    
    console.log('   - pdfData exists:', !!pdfData);
    console.log('   - practitionerData exists:', !!practitioner);
    console.log('   - referralId:', savedReferralId);
    console.log('   - savedReferral:', savedReferral);
    
    if (practitioner) {
      console.log('✅ Setting practitioner data:', {
        id: practitioner?.id,
        slug: practitioner?.slug,
        name: practitioner?.practitioner_name
      });
      setPractitionerData(practitioner);
    } else {
      console.warn('⚠️ No practitioner data received in confirmation page');
    }
    
    if (pdfData) {
      console.log('✅ Processing form data for display');
      const formattedData = {
        patientName: `${pdfData?.patientInfo?.lastName || ''} ${pdfData?.patientInfo?.firstName || ''}`?.trim() || 'N/A',
        patientLastName: pdfData?.patientInfo?.lastName || 'N/A',
        patientFirstName: pdfData?.patientInfo?.firstName || 'N/A',
        patientYearOfBirth: pdfData?.patientInfo?.yearOfBirth || 'N/A',
        patientSex: pdfData?.patientInfo?.sex === 'male' ? 'Homme' : pdfData?.patientInfo?.sex === 'female' ? 'Femme' : pdfData?.patientInfo?.sex === 'other' ? 'Autre' : 'Non spécifié',
        toothNumbers: pdfData?.toothNumbers || [],
        referralReasons: pdfData?.referralReasons || [],
        clinicalNotes: pdfData?.clinicalNotes || '',
        dentistName: pdfData?.referringDentist?.fullName || 'N/A',
        practiceName: pdfData?.referringDentist?.practiceName || 'Non spécifié',
        dentistEmail: pdfData?.referringDentist?.email || 'Non spécifié',
        dentistPhone: pdfData?.referringDentist?.phone || 'Non spécifié',
        imaging: pdfData?.imaging || {},
        uploadedFiles: pdfData?.uploadedFiles || [],
        submissionTime: new Date()?.toLocaleString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: false
        }),
        referenceNumber: `REF-${Date.now()?.toString()?.slice(-8)}`,
        pdfFileName: `Adressage_Endodontie_${pdfData?.patientInfo?.lastName || 'Patient'}_${Date.now()}.pdf`,
        // Store original pdfData and practitioner data for PDF generation
        originalPdfData: pdfData,
        practitionerData: practitioner
      };
      
      setSubmissionData(formattedData);
    }
  }, [location?.state]);

  const handlePrintForm = () => {
    if (!submissionData?.originalPdfData) {
      console.error('No PDF data available');
      return;
    }

    // Generate and download PDF for printing
    downloadReferralPDF(
      submissionData?.originalPdfData,
      submissionData?.patientLastName?.replace(/\s/g, '_') || 'Patient',
      submissionData?.referenceNumber,
      practitionerData
    );
  };

  const handleEmailToPractice = () => {
    const recipientEmail = practitionerData?.referral_email || practitionerData?.email || '';
    if (!recipientEmail) {
      alert('Email du praticien non disponible');
      return;
    }
    const subject = encodeURIComponent(`Fiche de correspondance - ${submissionData?.patientName || 'Patient'}`);
    const body = encodeURIComponent(
      `Bonjour,\n\nVeuillez trouver ci-joint la fiche de correspondance pour le patient ${submissionData?.patientName || 'N/A'}.\n\nRéférence: ${submissionData?.referenceNumber || 'N/A'}\n\nCordialement,\n${submissionData?.dentistName || 'Praticien'}`
    );
    window.location.href = `mailto:${recipientEmail}?subject=${subject}&body=${body}`;
  };

  const handleCall = () => {
    // Use practitioner's phone
    const phoneNumber = practitionerData?.phone || '';
    if (!phoneNumber) {
      alert('Numéro de téléphone du praticien non disponible');
      return;
    }
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleEmail = () => {
    // Use practitioner's email
    const email = practitionerData?.email || '';
    if (!email) {
      alert('Email du praticien non disponible');
      return;
    }
    window.location.href = `mailto:${email}`;
  };

  const handleNewReferral = () => {
    // Navigate back to the referral form for this practitioner
    const targetRoute = slug ? `/p/${slug}` : '/referral-form';
    navigate(targetRoute);
  };

  if (!submissionData) {
    return (
      <div className="loading-container">
        <Icon name="Loader2" size={48} className="loading-spinner" />
        <p className="loading-text">Chargement de la confirmation...</p>
      </div>
    );
  }

  return (
    <div className="submission-confirmation-page">
      {/* Premium gradient background matching login */}
      <div className="premium-background" />
      
      <div className="brand-header-confirmation animate-fade-in">
        <div className="header-content-wrapper-confirmation">
          <div className="at-logo-monogram-confirmation">
            {practitionerData?.logoInitials || practitionerData?.practitionerName?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase() || 'XX'}
          </div>
          <div className="header-text-content-confirmation">
            <h1 className="brand-title-confirmation">
              {practitionerData?.practitionerName 
                ? `${practitionerData?.practitionerTitle || 'Dr'} ${practitionerData?.practitionerName}` 
                : 'Praticien'}
            </h1>
            <p className="brand-subtitle-confirmation">
              {practitionerData?.practiceName || 'Endodontie exclusive'}
            </p>
          </div>
        </div>
      </div>
      
      <div className="confirmation-container">
        {/* Success Header with elegant animation */}
        <div className="confirmation-header">
          <div className="success-icon-wrapper">
            <div className="success-icon-circle">
              <Icon name="CheckCircle2" size={64} className="success-icon" />
            </div>
          </div>
          
          <h1 className="confirmation-title">
            Fiche de correspondance enregistrée
          </h1>
          
          <p className="confirmation-subtitle">
            Votre demande d'adressage a été finalisée avec succès.
          </p>
          
          {/* Reference Details */}
          <div className="reference-details glass-card rounded-2xl p-6">
            <div className="detail-row">
              <Icon name="Clock" size={18} className="detail-icon" />
              <span className="detail-label">Généré :</span>
              <span className="detail-value">{submissionData?.submissionTime}</span>
            </div>
            <div className="detail-row">
              <Icon name="FileText" size={18} className="detail-icon" />
              <span className="detail-label">Référence :</span>
              <span className="detail-value">{submissionData?.referenceNumber}</span>
            </div>
            <div className="detail-row">
              <Icon name="User" size={18} className="detail-icon" />
              <span className="detail-label">Patient :</span>
              <span className="detail-value">{submissionData?.patientName}</span>
            </div>
          </div>
        </div>

        {/* Instructions Section */}
        <div className="instructions-block glass-card rounded-2xl p-8 md:p-10 transition-smooth hover-glow">
          <h2 className="instructions-title text-xl md:text-2xl font-semibold text-foreground mb-6">
            Pensez à :
          </h2>
          
          <div className="instruction-items">
            {/* Print Instruction */}
            <div className="instruction-item">
              <div className="instruction-icon-wrapper print-icon">
                <Icon name="Printer" size={24} />
              </div>
              <div className="instruction-content">
                <h3 className="instruction-heading">Imprimer la fiche et la remettre au patient</h3>
                <p className="instruction-description">
                  Imprimez le document et remettez-le directement à votre patient pour qu'il puisse le présenter lors de sa consultation.
                </p>
              </div>
            </div>

            {/* Email Instruction */}
            <div className="instruction-item">
              <div className="instruction-icon-wrapper email-icon">
                <Icon name="Mail" size={24} />
              </div>
              <div className="instruction-content">
                <h3 className="instruction-heading">Email envoyé automatiquement au cabinet</h3>
                <p className="instruction-description">
                  Un email contenant la fiche de correspondance a été envoyé automatiquement au cabinet d'endodontie. Vous pouvez également l'envoyer manuellement si nécessaire en utilisant le bouton ci-dessous.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Appointment Reminder */}
        <div className="reminder-block glass-card rounded-2xl p-6 md:p-8 transition-smooth">
          <div className="reminder-header">
            <Icon name="Info" size={24} className="reminder-icon" />
            <h3 className="reminder-title">Rappel important</h3>
          </div>
          <p className="reminder-text">
            Le patient doit ensuite prendre rendez-vous lui-même auprès du cabinet d'endodontie. La fiche de correspondance ne constitue pas une réservation automatique.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons-block glass-card rounded-2xl p-8 md:p-10 transition-smooth hover-glow">
          <div className="action-buttons">
            <div className="action-buttons-group-confirmation">
              <Button
                variant="default"
                size="lg"
                onClick={handlePrintForm}
                iconName="Printer"
                className="action-button-confirmation"
              >
                Imprimer la fiche
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={handleEmailToPractice}
                iconName="Mail"
                className="action-button-confirmation"
              >
                Envoyer par mail
              </Button>
            </div>
          </div>
        </div>

        {/* New Referral Button */}
        <div className="glass-card rounded-2xl p-6 md:p-8 transition-smooth">
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-muted-foreground text-center">
              Vous avez un autre patient à adresser ?
            </p>
            <Button
              variant="outline"
              size="lg"
              onClick={handleNewReferral}
              iconName="Plus"
              iconPosition="left"
              className="w-full md:w-auto hover:bg-primary/15 hover:border-primary/50 hover:text-primary transition-all duration-300"
            >
              Soumettre un nouveau formulaire
            </Button>
          </div>
        </div>

        {/* Collapsible Summary */}
        <div className="collapsible-summary glass-card rounded-2xl overflow-hidden transition-smooth">
          <button
            className="summary-toggle"
            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
            aria-expanded={isSummaryExpanded}
          >
            <span className="summary-toggle-text">Voir le résumé de l'adressage</span>
            <Icon
              name={isSummaryExpanded ? "ChevronUp" : "ChevronDown"}
              size={20}
              className="summary-toggle-icon"
            />
          </button>
          
          {isSummaryExpanded && (
            <div className="summary-content">
              <div className="summary-item">
                <span className="summary-label">Nom du patient :</span>
                <span className="summary-value">{submissionData?.patientLastName}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Prénom du patient :</span>
                <span className="summary-value">{submissionData?.patientFirstName}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Année de naissance :</span>
                <span className="summary-value">{submissionData?.patientYearOfBirth}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Dent(s) concernée(s) :</span>
                <div className="tooth-tags">
                  {submissionData?.toothNumbers?.map((tooth, index) => (
                    <span key={index} className="tooth-tag">{tooth}</span>
                  ))}
                </div>
              </div>
              <div className="summary-item">
                <span className="summary-label">Motif(s) d'adressage :</span>
                <div className="reason-list">
                  {submissionData?.referralReasons?.map((reason, index) => (
                    <div key={index} className="reason-item">
                      <Icon name="ChevronRight" size={16} className="reason-icon" />
                      <span>{reason}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="summary-item">
                <span className="summary-label">Confrère adresseur :</span>
                <span className="summary-value">{submissionData?.dentistName}</span>
              </div>
            </div>
          )}
        </div>

        {/* Contact Footer */}
        <div className="light-footer glass-card rounded-2xl">
          <p className="footer-message">Besoin d'une modification ? Contactez le cabinet.</p>
          <div className="footer-buttons">
            <Button
              variant="outline"
              size="md"
              iconName="Phone"
              iconPosition="left"
              onClick={handleCall}
            >
              Appeler le cabinet
            </Button>
            <Button
              variant="outline"
              size="md"
              iconName="Mail"
              iconPosition="left"
              onClick={handleEmail}
            >
              Envoyer un email
            </Button>
          </div>
        </div>
      </div>
      
      <Footer practitionerData={practitionerData} />
      
      <style jsx>{`
        .submission-confirmation-page {
          min-height: 100vh;
          background: #1A1A1E;
          padding-bottom: 2rem;
          position: relative;
          overflow-x: hidden;
        }

        /* Premium gradient background matching login page exactly */
        .premium-background {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom right, rgba(37, 99, 235, 0.15) 0%, #1A1A1E 50%, #1A1A1E 100%);
          z-index: 0;
          pointer-events: none;
        }

        /* Smooth fade-in animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        .brand-header-confirmation {
          background: rgba(26, 26, 30, 0.8);
          backdrop-filter: blur(40px) saturate(180%);
          -webkit-backdrop-filter: blur(40px) saturate(180%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding: 1.5rem 1rem;
          position: sticky;
          top: 0;
          z-index: 50;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
        }

        .header-content-wrapper-confirmation {
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .at-logo-monogram-confirmation {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.1) 100%);
          backdrop-filter: blur(20px);
          color: #FFFFFF;
          border: 1px solid rgba(96, 165, 250, 0.3);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.75rem;
          font-weight: 700;
          letter-spacing: 0.05em;
          flex-shrink: 0;
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.6),
            0 0 20px rgba(59, 130, 246, 0.2),
            0 0 0 1px rgba(255, 255, 255, 0.05) inset;
        }

        .header-text-content-confirmation {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .brand-title-confirmation {
          font-size: 1.75rem;
          font-weight: 600;
          color: #FFFFFF;
          margin: 0;
          letter-spacing: -0.03em;
        }

        .brand-subtitle-confirmation {
          font-size: 0.95rem;
          color: #9CA3AF;
          margin: 0;
          font-weight: 400;
          letter-spacing: 0.01em;
        }

        .confirmation-container {
          max-width: 56rem;
          margin: 0 auto;
          padding: 2rem 1rem;
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .loading-container {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          background: #1A1A1E;
        }

        .loading-spinner {
          color: #3B82F6;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .loading-text {
          font-size: 1rem;
          color: #9CA3AF;
        }

        .confirmation-header {
          text-align: center;
          padding: 2rem 1rem;
        }

        .success-icon-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 1.5rem;
        }

        .success-icon-circle {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .success-icon-circle::before {
          content: '';
          position: absolute;
          width: 100px;
          height: 100px;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          animation: pulseGlow 2s ease-out infinite;
        }

        @keyframes pulseGlow {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.3;
          }
        }

        .success-icon {
          color: #10B981;
          position: relative;
          z-index: 1;
          filter: drop-shadow(0 0 20px rgba(16, 185, 129, 0.4));
          animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        @keyframes scaleIn {
          0% {
            transform: scale(0) rotate(-180deg);
            opacity: 0;
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }

        .confirmation-title {
          font-size: 1.875rem;
          font-weight: 600;
          color: #FFFFFF;
          margin-bottom: 0.75rem;
          letter-spacing: -0.02em;
        }

        .confirmation-subtitle {
          font-size: 1rem;
          color: #D1D5DB;
          line-height: 1.6;
          margin-bottom: 2rem;
        }

        .glass-card {
          background: rgba(26, 26, 31, 0.6);
          backdrop-filter: blur(40px) saturate(180%);
          -webkit-backdrop-filter: blur(40px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(255, 255, 255, 0.05) inset;
        }

        .transition-smooth {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .hover-glow:hover {
          border-color: rgba(96, 165, 250, 0.3);
          box-shadow: 
            0 12px 40px rgba(59, 130, 246, 0.2),
            0 0 0 1px rgba(255, 255, 255, 0.08) inset;
        }

        .reference-details {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-top: 2rem;
          animation: fadeInUp 0.5s ease-out 0.2s both;
        }

        .detail-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.9375rem;
        }

        .detail-icon {
          color: #60A5FA;
          flex-shrink: 0;
        }

        .detail-label {
          color: #9CA3AF;
          font-weight: 500;
        }

        .detail-value {
          color: #FFFFFF;
          font-weight: 600;
          margin-left: auto;
        }

        .instructions-block {
          animation: fadeInUp 0.5s ease-out 0.3s both;
        }

        .instructions-title {
          color: #FFFFFF;
        }

        .instruction-items {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .instruction-item {
          display: flex;
          gap: 1rem;
          align-items: flex-start;
        }

        .instruction-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }

        .print-icon {
          background: rgba(59, 130, 246, 0.15);
          color: #60A5FA;
          border: 1px solid rgba(96, 165, 250, 0.2);
        }

        .email-icon {
          background: rgba(139, 92, 246, 0.15);
          color: #A78BFA;
          border: 1px solid rgba(167, 139, 250, 0.2);
        }

        .instruction-content {
          flex: 1;
        }

        .instruction-heading {
          font-size: 1rem;
          font-weight: 600;
          color: #FFFFFF;
          margin: 0 0 0.5rem 0;
        }

        .instruction-description {
          font-size: 0.875rem;
          color: #D1D5DB;
          line-height: 1.6;
          margin: 0;
        }

        .reminder-block {
          animation: fadeInUp 0.5s ease-out 0.4s both;
          background: rgba(245, 158, 11, 0.08);
          border-color: rgba(245, 158, 11, 0.25);
        }

        .reminder-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .reminder-icon {
          color: #FBBF24;
          flex-shrink: 0;
        }

        .reminder-title {
          font-size: 1rem;
          font-weight: 600;
          color: #FFFFFF;
          margin: 0;
        }

        .reminder-text {
          font-size: 0.9375rem;
          color: #E5E7EB;
          line-height: 1.6;
          margin: 0;
          padding-left: 2.25rem;
        }

        .action-buttons-block {
          animation: fadeInUp 0.5s ease-out 0.5s both;
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .action-buttons-group-confirmation {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .action-button-confirmation {
          width: 100%;
        }

        .collapsible-summary {
          animation: fadeInUp 0.5s ease-out 0.6s both;
        }

        .summary-toggle {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem 1.75rem;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: all 400ms cubic-bezier(0.25, 0.1, 0.25, 1);
        }

        .summary-toggle:hover {
          background: rgba(59, 130, 246, 0.08);
        }

        .summary-toggle-text {
          font-size: 1rem;
          font-weight: 600;
          color: #FFFFFF;
        }

        .summary-toggle-icon {
          color: #60A5FA;
          transition: transform 0.3s;
        }

        .summary-content {
          padding: 1.5rem 1.75rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          animation: slideDown 0.4s ease-out;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .summary-item {
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .summary-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #9CA3AF;
        }

        .summary-value {
          font-size: 1rem;
          font-weight: 600;
          color: #FFFFFF;
        }

        .tooth-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.625rem;
        }

        .tooth-tag {
          display: inline-flex;
          align-items: center;
          padding: 0.625rem 1rem;
          background: rgba(59, 130, 246, 0.15);
          color: #93C5FD;
          border: 1px solid rgba(96, 165, 250, 0.3);
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 600;
          font-family: 'JetBrains Mono', monospace;
          backdrop-filter: blur(20px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .reason-list {
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .reason-item {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          font-size: 0.9375rem;
          color: #FFFFFF;
        }

        .reason-icon {
          color: #60A5FA;
          flex-shrink: 0;
        }

        .light-footer {
          padding: 1.75rem;
          text-align: center;
          animation: fadeInUp 0.5s ease-out 0.7s both;
        }

        .footer-message {
          font-size: 0.9375rem;
          color: #D1D5DB;
          margin: 0 0 1.25rem 0;
        }

        .footer-buttons {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        @media (min-width: 640px) {
          .footer-buttons {
            flex-direction: row;
            justify-content: center;
            gap: 1rem;
          }
        }

        @media (min-width: 768px) {
          .brand-header-confirmation {
            padding: 2rem 1.5rem;
          }

          .confirmation-container {
            padding: 3rem 1.5rem;
            gap: 2rem;
          }

          .confirmation-header {
            padding: 3rem 1.5rem;
          }

          .confirmation-title {
            font-size: 2.25rem;
          }

          .confirmation-subtitle {
            font-size: 1.125rem;
          }

          .reference-details {
            flex-direction: row;
            justify-content: center;
            gap: 2rem;
          }

          .detail-value {
            margin-left: 0.5rem;
          }

          .instruction-items {
            gap: 2rem;
          }

          .instruction-icon-wrapper {
            width: 56px;
            height: 56px;
          }

          .instruction-heading {
            font-size: 1.125rem;
          }

          .action-buttons-group-confirmation {
            flex-direction: row;
            gap: 1.25rem;
          }

          .summary-content {
            padding: 2rem 2.25rem;
          }

          .light-footer {
            padding: 2rem;
          }
        }

        @media (min-width: 1024px) {
          .confirmation-container {
            padding: 4rem 2rem;
          }

          .confirmation-title {
            font-size: 2.5rem;
          }
        }

        @media (max-width: 768px) {
          .brand-header-confirmation {
            padding: 1.5rem 1rem;
          }

          .at-logo-monogram-confirmation {
            width: 56px;
            height: 56px;
            font-size: 1.5rem;
          }

          .brand-title-confirmation {
            font-size: 1.5rem;
          }

          .brand-subtitle-confirmation {
            font-size: 0.875rem;
          }

          .confirmation-container {
            padding: 2rem 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default SubmissionConfirmation;